import React from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import { DeleteOutline as DeleteOutlineIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Team } from '../../models/types';

export const ALL = '__all__';
export const UNASSIGNED = '__unassigned__';

interface TeamRosterFilterChipsProps {
  teams: Team[];
  // Row count per team id, plus UNASSIGNED.
  counts: Map<string, number>;
  total: number;
  value: string;
  onChange: (value: string) => void;
  // Absent when the roster is locked.
  onDeleteTeam?: (team: Team) => void;
}

// One chip per team doubles as the roster filter and a team summary; team chips carry the delete action.
export const TeamRosterFilterChips = ({ teams, counts, total, value, onChange, onDeleteTeam }: TeamRosterFilterChipsProps) => {
  const { t } = useTranslation('contests');
  const chip = (key: string, label: string, onDelete?: () => void, deleteLabel?: string) => (
    <Chip
      key={key}
      size="small"
      label={label}
      clickable
      color={value === key ? 'primary' : 'default'}
      variant={value === key ? 'filled' : 'outlined'}
      onClick={() => onChange(key)}
      onDelete={onDelete}
      deleteIcon={deleteLabel ? <Tooltip title={deleteLabel}><DeleteOutlineIcon aria-label={deleteLabel} /></Tooltip> : undefined}
      aria-pressed={value === key}
    />
  );

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }} role="group" aria-label={t('roster.filterLabel')}>
      {chip(ALL, t('roster.allCount', { count: total }))}
      {chip(UNASSIGNED, t('roster.unassignedCount', { count: counts.get(UNASSIGNED) ?? 0 }))}
      {teams.map(team =>
        chip(team._id, t('roster.teamCount', { team: team.name, count: counts.get(team._id) ?? 0 }), onDeleteTeam ? () => onDeleteTeam(team) : undefined, t('roster.deleteTeam', { team: team.name }))
      )}
    </Box>
  );
};
